import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Topbar from '../components/Topbar';
import api from '../api';

const TYPES = [
  { value: 'texte_court', label: 'Texte court', icon: 'bi-input-cursor-text', color: '#e8f0fe' },
  { value: 'texte_long',  label: 'Texte long',  icon: 'bi-textarea-t',        color: '#cef2e7' },
  { value: 'choix_unique',   label: 'Choix unique',   icon: 'bi-ui-radios',     color: '#fdf3d5' },
  { value: 'choix_multiple', label: 'Choix multiple', icon: 'bi-ui-checks',     color: '#ede9fe' },
  { value: 'liste',    label: 'Liste déroulante', icon: 'bi-menu-button-wide', color: '#ffedd5' },
  { value: 'note',     label: 'Note (étoiles)',   icon: 'bi-star-half',         color: '#fce7f3' },
  { value: 'date',     label: 'Date',             icon: 'bi-calendar3',         color: '#d1ecf1' },
  { value: 'nombre',   label: 'Nombre',           icon: 'bi-123',               color: '#f7d4d8' },
  { value: 'separateur', label: 'Séparateur / Titre', icon: 'bi-dash-lg',      color: '#ebeced' },
];

function newQuestion(type = 'texte_court') {
  return { _id: String(Date.now() + Math.random()), libelle: '', type, obligatoire: false, placeholder: '', options: [] };
}

export default function FormulaireBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const questionsEndRef = useRef(null);

  useEffect(() => {
    if (isEdit) {
      api.get(`/formulaires/${id}`).then(({ data }) => {
        setTitre(data.titre);
        setDescription(data.description || '');
        setQuestions(data.questions.map(q => ({ ...q, _id: String(q.id) })));
      });
    }
  }, [id]);

  const addQuestion = (type) => {
    setQuestions(qs => [...qs, newQuestion(type)]);
    setTimeout(() => questionsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions(qs => qs.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const removeQuestion = (idx) => {
    setQuestions(qs => qs.filter((_, i) => i !== idx));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setQuestions(qs => {
      const arr = [...qs];
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return arr;
    });
  };

  const addOption = (qIdx) => {
    setQuestions(qs => qs.map((q, i) => i === qIdx
      ? { ...q, options: [...q.options, { libelle: '' }] }
      : q
    ));
  };

  const updateOption = (qIdx, oIdx, value) => {
    setQuestions(qs => qs.map((q, i) => i === qIdx
      ? { ...q, options: q.options.map((o, j) => j === oIdx ? { ...o, libelle: value } : o) }
      : q
    ));
  };

  const removeOption = (qIdx, oIdx) => {
    setQuestions(qs => qs.map((q, i) => i === qIdx
      ? { ...q, options: q.options.filter((_, j) => j !== oIdx) }
      : q
    ));
  };

  const handleSave = async () => {
    if (!titre.trim()) { setError('Le titre est requis'); return; }
    if (questions.length === 0) { setError('Ajoutez au moins une question'); return; }
    for (const q of questions) {
      if (q.type !== 'separateur' && !q.libelle.trim()) {
        setError('Toutes les questions doivent avoir un libellé'); return;
      }
      if (['choix_unique','choix_multiple','liste'].includes(q.type) && q.options.length < 2) {
        setError(`La question "${q.libelle}" doit avoir au moins 2 options`); return;
      }
    }
    setError('');
    setSaving(true);
    try {
      const payload = { titre, description, questions };
      if (isEdit) {
        await api.put(`/formulaires/${id}`, payload);
        navigate(`/formulaire/${id}`);
      } else {
        const { data } = await api.post('/formulaires', payload);
        navigate(`/formulaire/${data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
      setSaving(false);
    }
  };

  return (
    <>
      <Topbar
        title={isEdit ? 'Modifier le formulaire' : 'Créer un formulaire'}
        actions={
          <>
            <button className="btn btn-light" onClick={() => navigate(-1)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Enregistrement...</> : <><i className="bi bi-check2 me-1"></i>Enregistrer</>}
            </button>
          </>
        }
      />
      <div className="page-content">
        {error && (
          <div className="alert alert-danger"><i className="bi bi-exclamation-circle me-2"></i>{error}</div>
        )}

        <div className="row g-4">
          {/* Panneau gauche */}
          <div className="col-lg-4">
            <div style={{ position: 'sticky', top: 16 }}>
              <div className="card mb-3">
                <div className="card-header"><span className="fw-semibold">Informations</span></div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label fw-medium" style={{ fontSize: '0.875rem' }}>Titre du formulaire *</label>
                    <input className="form-control" value={titre} onChange={e => setTitre(e.target.value)} placeholder="Ex: Fiche de rentrée 2025" />
                  </div>
                  <div>
                    <label className="form-label fw-medium" style={{ fontSize: '0.875rem' }}>Description (optionnel)</label>
                    <textarea className="form-control" rows={3} value={description} onChange={e => setDescription(e.target.value)} placeholder="Instructions pour les élèves..." />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><span className="fw-semibold">Ajouter un champ</span></div>
                <div className="card-body p-2">
                  <div className="d-flex flex-wrap gap-2">
                    {TYPES.map(t => (
                      <button
                        key={t.value}
                        className="btn btn-sm d-flex align-items-center gap-1"
                        style={{ background: t.color, border: 'none', fontSize: '0.78rem', borderRadius: 8 }}
                        onClick={() => addQuestion(t.value)}
                        title={t.label}
                      >
                        <i className={`bi ${t.icon}`}></i>
                        <span>{t.label}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.72rem' }}>
                    <i className="bi bi-grip-vertical me-1"></i>Glisser-déposer pour réordonner
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Panneau droit : questions */}
          <div className="col-lg-8">
            {questions.length === 0 ? (
              <div className="card" style={{ background: '#fafbfc' }}>
                <div className="card-body text-center py-5 text-muted">
                  <i className="bi bi-cursor-fill" style={{ fontSize: '2.5rem', opacity: 0.2 }}></i>
                  <p className="mt-3 mb-0">Cliquez sur un type de champ à gauche pour ajouter une question</p>
                </div>
              </div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="questions">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {questions.map((q, idx) => {
                        const typeMeta = TYPES.find(t => t.value === q.type);
                        const canToggleType = q.type === 'texte_court' || q.type === 'texte_long';
                        return (
                          <Draggable key={q._id} draggableId={q._id} index={idx}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`question-card ${snapshot.isDragging ? 'dragging' : ''}`}
                              >
                                <div className="d-flex align-items-center gap-2 p-3 pb-2">
                                  <span {...provided.dragHandleProps} style={{ cursor: 'grab', color: '#adb5bd', fontSize: '1.1rem', lineHeight: 1 }}>
                                    <i className="bi bi-grip-vertical"></i>
                                  </span>

                                  {canToggleType ? (
                                    <button
                                      className="question-type-badge"
                                      style={{ background: typeMeta?.color || '#eee', border: 'none', cursor: 'pointer' }}
                                      title="Cliquer pour basculer entre texte court et texte long"
                                      onClick={() => updateQuestion(idx, 'type', q.type === 'texte_court' ? 'texte_long' : 'texte_court')}
                                    >
                                      <i className={`bi ${typeMeta?.icon} me-1`}></i>{typeMeta?.label}
                                      <i className="bi bi-arrow-left-right ms-1" style={{ opacity: 0.5, fontSize: '0.6rem' }}></i>
                                    </button>
                                  ) : (
                                    <span className="question-type-badge" style={{ background: typeMeta?.color || '#eee' }}>
                                      <i className={`bi ${typeMeta?.icon} me-1`}></i>{typeMeta?.label}
                                    </span>
                                  )}

                                  <div className="ms-auto d-flex gap-1 align-items-center">
                                    {q.type !== 'separateur' && (
                                      <div className="form-check form-check-inline mb-0 me-1">
                                        <input type="checkbox" className="form-check-input" id={`obl-${idx}`}
                                          checked={q.obligatoire} onChange={e => updateQuestion(idx, 'obligatoire', e.target.checked)} />
                                        <label className="form-check-label" htmlFor={`obl-${idx}`} style={{ fontSize: '0.78rem' }}>Requis</label>
                                      </div>
                                    )}
                                    <button className="btn btn-sm btn-light text-danger" onClick={() => removeQuestion(idx)}>
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </div>

                                <div className="px-3 pb-3">
                                  {q.type === 'separateur' ? (
                                    <input className="form-control fw-semibold" value={q.libelle}
                                      onChange={e => updateQuestion(idx, 'libelle', e.target.value)}
                                      placeholder="Titre de section..." />
                                  ) : (
                                    <>
                                      <input className="form-control mb-2" value={q.libelle}
                                        onChange={e => updateQuestion(idx, 'libelle', e.target.value)}
                                        placeholder="Question / libellé du champ..." />

                                      {['texte_court','texte_long','nombre'].includes(q.type) && (
                                        <input className="form-control form-control-sm" value={q.placeholder}
                                          onChange={e => updateQuestion(idx, 'placeholder', e.target.value)}
                                          placeholder="Placeholder (optionnel)" />
                                      )}

                                      {['choix_unique','choix_multiple','liste'].includes(q.type) && (
                                        <div className="mt-2">
                                          {q.options.map((o, oi) => (
                                            <div key={oi} className="d-flex gap-2 mb-1">
                                              <input className="form-control form-control-sm" value={o.libelle}
                                                onChange={e => updateOption(idx, oi, e.target.value)}
                                                placeholder={`Option ${oi + 1}`} />
                                              <button className="btn btn-sm btn-light text-danger" onClick={() => removeOption(idx, oi)}>
                                                <i className="bi bi-x"></i>
                                              </button>
                                            </div>
                                          ))}
                                          <button className="btn btn-sm btn-light mt-1" onClick={() => addOption(idx)}>
                                            <i className="bi bi-plus me-1"></i>Ajouter une option
                                          </button>
                                        </div>
                                      )}

                                      {q.type === 'note' && (
                                        <div className="d-flex gap-1 mt-2">
                                          {[1,2,3,4,5].map(s => (
                                            <i key={s} className="bi bi-star-fill" style={{ color: '#f7c32e', fontSize: '1.3rem' }}></i>
                                          ))}
                                          <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>Prévisualisation</span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            <div ref={questionsEndRef} />
          </div>
        </div>
      </div>
    </>
  );
}
